/**
 * Modal for filling in customization fields when adding an item to a Sales Order.
 *
 * - Fetches the item's customization template via get_item_customization API
 * - Runs the rule engine on every value change (useMemo, pure)
 * - Auto-skips (calls onSkip) if the item has no template
 * - Supports both single items and bundles
 * - Returns SalesOrderItemCustomization[] entries on submit
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Pencil } from 'lucide-react';
import { useItemCustomization } from '../hooks/useItemCustomization';
import { evaluateRules } from '../lib/rule-engine';
import { CustomizationFieldRenderer } from './CustomizationFieldRenderer';
import type {
  CustomizationField,
  PersonalizationValues,
  SingleItemCustomization,
  BundleCustomization,
} from '../lib/types';
import type { SalesOrderItemCustomization } from '@/lib/erp/types/order';

interface CustomizationFormModalProps {
  itemCode: string | null;
  itemName?: string;
  company?: string;
  open: boolean;
  onSubmit: (customizations: SalesOrderItemCustomization[]) => void;
  onSkip: () => void;
}

export function CustomizationFormModal({
  itemCode,
  itemName,
  company,
  open,
  onSubmit,
  onSkip,
}: CustomizationFormModalProps) {
  const { data, isLoading, isError } = useItemCustomization(open ? itemCode : null, company);

  // Auto-skip: no template for this item
  const hasSkipped = useMemo(() => {
    if (!open || isLoading || isError || !data) return false;
    if (!data.is_bundle) {
      const single = data as SingleItemCustomization;
      return !single.fields || single.fields.length === 0;
    }
    const bundle = data as BundleCustomization;
    return !bundle.bundle_items || bundle.bundle_items.length === 0;
  }, [open, isLoading, isError, data]);

  useEffect(() => {
    if (hasSkipped) onSkip();
  }, [hasSkipped, onSkip]);

  if (!open || hasSkipped) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onSkip(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-amber-600" />
            <span>Personalization</span>
            {itemName && (
              <span className="text-muted-foreground font-normal text-sm ml-1 truncate">
                — {itemName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="text-sm text-red-500 py-4">
            Failed to load customization template.
          </div>
        )}

        {data && !isLoading && !isError && (
          <CustomizationFormContent
            data={data}
            onSubmit={onSubmit}
            onCancel={onSkip}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Inner component that manages form state — only rendered when data is loaded
interface FormContentProps {
  data: SingleItemCustomization | BundleCustomization;
  onSubmit: (customizations: SalesOrderItemCustomization[]) => void;
  onCancel: () => void;
}

function CustomizationFormContent({ data, onSubmit, onCancel }: FormContentProps) {
  if (data.is_bundle) {
    return <BundleForm data={data as BundleCustomization} onSubmit={onSubmit} onCancel={onCancel} />;
  }
  return <SingleItemForm data={data as SingleItemCustomization} onSubmit={onSubmit} onCancel={onCancel} />;
}

// Single item form
function SingleItemForm({
  data,
  onSubmit,
  onCancel,
}: {
  data: SingleItemCustomization;
  onSubmit: (customizations: SalesOrderItemCustomization[]) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<PersonalizationValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const ruleResult = useMemo(
    () => evaluateRules(data.fields, data.rules, values),
    [data.fields, data.rules, values],
  );

  // Apply forced values
  useEffect(() => {
    const updates: PersonalizationValues = {};
    for (const [label, forced] of Object.entries(ruleResult.forced)) {
      if (forced !== null && values[label] !== forced) {
        updates[label] = forced;
      }
    }
    if (Object.keys(updates).length > 0) {
      setValues((prev) => ({ ...prev, ...updates }));
    }
  }, [ruleResult.forced, values]);

  const handleChange = useCallback((label: string, val: string) => {
    setValues((prev) => ({ ...prev, [label]: val }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[label];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    // Validate required visible fields
    const newErrors: Record<string, string> = {};
    for (const field of data.fields) {
      const label = field.label;
      if (ruleResult.visible[label] && ruleResult.required[label]) {
        if (!values[label]?.trim()) {
          newErrors[label] = 'This field is required';
        }
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build customization entries
    const entries: SalesOrderItemCustomization[] = [];
    for (const field of data.fields) {
      if (!ruleResult.visible[field.label]) continue;
      const val = values[field.label] || '';
      if (!val) continue;

      entries.push({
        parent_item_row: '', // Will be set by caller
        field_label: field.label,
        field_type: field.field_type,
        value: field.field_type === 'File Upload' ? '' : val,
        file_url: field.field_type === 'File Upload' ? val : undefined,
        addon_item_code: field.addon_item || undefined,
        addon_amount: field.addon_price || undefined,
      });
    }

    onSubmit(entries);
  }, [data.fields, ruleResult, values, onSubmit]);

  return (
    <FormFields
      fields={data.fields}
      values={values}
      ruleResult={ruleResult}
      errors={errors}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  );
}

// Bundle form — renders sections per child item
function BundleForm({
  data,
  onSubmit,
  onCancel,
}: {
  data: BundleCustomization;
  onSubmit: (customizations: SalesOrderItemCustomization[]) => void;
  onCancel: () => void;
}) {
  // One values dict per bundle child, keyed by child item_code
  const [allValues, setAllValues] = useState<Record<string, PersonalizationValues>>({});
  const [allErrors, setAllErrors] = useState<Record<string, Record<string, string>>>({});

  const handleChange = useCallback((childCode: string, label: string, val: string) => {
    setAllValues((prev) => ({
      ...prev,
      [childCode]: { ...(prev[childCode] || {}), [label]: val },
    }));
    setAllErrors((prev) => {
      const childErrors = { ...(prev[childCode] || {}) };
      delete childErrors[label];
      return { ...prev, [childCode]: childErrors };
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const allEntries: SalesOrderItemCustomization[] = [];
    const newAllErrors: Record<string, Record<string, string>> = {};
    let hasErrors = false;

    for (const child of data.bundle_items) {
      const childValues = allValues[child.item_code] || {};
      const result = evaluateRules(child.fields, child.rules, childValues);

      const childErrors: Record<string, string> = {};
      for (const field of child.fields) {
        if (result.visible[field.label] && result.required[field.label]) {
          if (!childValues[field.label]?.trim()) {
            childErrors[field.label] = 'This field is required';
            hasErrors = true;
          }
        }
      }
      if (Object.keys(childErrors).length > 0) {
        newAllErrors[child.item_code] = childErrors;
      }

      for (const field of child.fields) {
        if (!result.visible[field.label]) continue;
        const val = childValues[field.label] || '';
        if (!val) continue;

        // Use colon-separated key format matching backend parser:
        // shared fields → "shared:Label", per-child → "ITEM_CODE:Label"
        const fieldKey = field.shared
          ? `shared:${field.label}`
          : `${child.item_code}:${field.label}`;

        allEntries.push({
          parent_item_row: '',
          field_label: fieldKey,
          field_type: field.field_type,
          value: field.field_type === 'File Upload' ? '' : val,
          file_url: field.field_type === 'File Upload' ? val : undefined,
          addon_item_code: field.addon_item || undefined,
          addon_amount: field.addon_price || undefined,
        });
      }
    }

    if (hasErrors) {
      setAllErrors(newAllErrors);
      return;
    }

    onSubmit(allEntries);
  }, [data.bundle_items, allValues, onSubmit]);

  return (
    <div className="space-y-6">
      {data.bundle_items.map((child) => {
        const childValues = allValues[child.item_code] || {};
        const childErrors = allErrors[child.item_code] || {};
        const result = evaluateRules(child.fields, child.rules, childValues);

        return (
          <div key={child.item_code}>
            <div className="text-sm font-medium text-foreground/80 mb-2">
              {child.item_name} ({child.item_code})
            </div>
            <FormFields
              fields={child.fields}
              values={childValues}
              ruleResult={result}
              errors={childErrors}
              onChange={(label, val) => handleChange(child.item_code, label, val)}
              hideActions
            />
          </div>
        );
      })}

      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" className="rounded-full px-6" onClick={onCancel}>
          Skip
        </Button>
        <Button className="rounded-full px-6" onClick={handleSubmit}>
          Add to Order
        </Button>
      </div>
    </div>
  );
}

// Shared field rendering + actions
function FormFields({
  fields,
  values,
  ruleResult,
  errors,
  onChange,
  onSubmit,
  onCancel,
  hideActions,
}: {
  fields: CustomizationField[];
  values: PersonalizationValues;
  ruleResult: ReturnType<typeof evaluateRules>;
  errors: Record<string, string>;
  onChange: (label: string, value: string) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  hideActions?: boolean;
}) {
  return (
    <div className="space-y-4">
      {fields.map((field) => {
        if (!ruleResult.visible[field.label]) return null;
        return (
          <CustomizationFieldRenderer
            key={field.label}
            field={field}
            value={values[field.label] || ''}
            onChange={(val) => onChange(field.label, val)}
            error={errors[field.label]}
            forcedValue={ruleResult.forced[field.label]}
            required={ruleResult.required[field.label]}
          />
        );
      })}

      {!hideActions && (
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" className="rounded-full px-6" onClick={onCancel}>
            Skip
          </Button>
          <Button className="rounded-full px-6" onClick={onSubmit}>
            Add to Order
          </Button>
        </div>
      )}
    </div>
  );
}
